import { useQuery } from '@tanstack/react-query';
import { memberInfoKey, requestMyMemberInfo } from '@/hooks/member/memberInfoApi';
import { mapToProfile } from './profileApi';

/**
 * The authenticated member's own profile, read from the shared `myMemberInfo` query/cache — same
 * key as {@link useCurrentBalance}/{@link useMemberAutoPaySummary}/{@link useMemberBillingProfile}
 * (`select` differs). Mounting this hook alongside one of those still doesn't fire a *second*
 * concurrent request — TanStack Query dedupes same-key fetches in flight — but see
 * `refetchOnMount` below: this hook's own mount always triggers a request of its own, unlike
 * theirs.
 *
 * `refetchOnMount: 'always'` overrides the app-wide 5-minute `staleTime` for this one observer —
 * the other hooks sharing this key don't set it, so they're unaffected. It exists for
 * `profilePhotoUrl`: its SAS URL is deliberately short-lived (30s server-side, sized for exactly
 * one render), far shorter than the 5-minute default the rest of `myMemberInfo` can tolerate. A
 * cached response from an earlier mount would hand the avatar an already-expired URL, which
 * fails to load and silently falls back to initials — indistinguishable from "no photo set."
 * Forcing a fresh fetch on every mount of this hook keeps the URL within its real usable window.
 */
export function useMyProfile() {
  const { data, isPending, isError, error } = useQuery({
    queryKey: memberInfoKey,
    queryFn: requestMyMemberInfo,
    select: mapToProfile,
    refetchOnMount: 'always',
  });

  // Exposes `error` (not just the isError flag) so the caller can render the specific failure via
  // extractGqlErrorCode + useErrorTranslation instead of a generic message.
  return { data, isPending, isError, error };
}
